import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { AmplitudeEvent } from 'src/app/interface/amplitude.interface';
import {
  InAppCampaignActionType,
  InAppCampaignCloseEvent,
  InAppCampaignCloseReason,
  InAppCampaignModal,
} from 'src/app/interface/in-app-campaign.interface';
import { AmplitudeService } from 'src/app/services/amplitude.service';
import { DataService } from 'src/app/services/data.service';
import { InAppCampaignService } from 'src/app/services/in-app-campaign.service';

/**
 * Mounted exactly once in `layouts/full/full.component.html`, next to `<app-menu-shell>` — never
 * in `layouts/blank`. Triggers resolution ONLY by subscribing to `dataService.buildingChanged$()`,
 * a `BehaviorSubject` that emits immediately on subscribe — see design.md's "Host" section and
 * `support-whatsapp.service.ts:50`'s precedent for the same pattern.
 *
 * That subject emits **several times per login**: once on subscribe, then again on every
 * `setBuilding()`. Each emission used to be harmless because RN-1 capped the user at one modal per
 * day globally, so every resolve after the first returned `null`. Since the RN-1 amendment the cap
 * is per campaign, so each extra emission surfaced a *different* campaign and the admin got the
 * whole backlog in one sitting. Hence the latch below: one campaign per building per page load.
 */
@Component({
  selector: 'app-in-app-campaign-host',
  template: `
    <app-in-app-campaign-modal
      *ngIf="campaign"
      [campaign]="campaign"
      (closed)="onModalClosed($event)"
    ></app-in-app-campaign-modal>
  `,
})
export class InAppCampaignHostComponent implements OnInit, OnDestroy {
  campaign: InAppCampaignModal | null = null;

  private _sub: Subscription | null = null;
  /**
   * Buildings already resolved during this page load. A resolve is not free: the backend reserves
   * the impression and counts the view, so a duplicate call both shows a second modal and inflates
   * the campaign's metrics with a view nobody saw.
   */
  private readonly _resolvedBuildingIds = new Set<string>();

  constructor(
    private readonly _dataService: DataService,
    private readonly _service: InAppCampaignService,
    private readonly _amplitudeService: AmplitudeService,
  ) {}

  ngOnInit(): void {
    this._sub = this._dataService.buildingChanged$().subscribe(() => this._tryResolve());
  }

  ngOnDestroy(): void {
    this._sub?.unsubscribe();
  }

  onModalClosed(event: InAppCampaignCloseEvent): void {
    const campaign = this.campaign;
    this.campaign = null;
    if (!campaign) return;

    const userId = this._currentUserId();
    if (event.reason === InAppCampaignCloseReason.click) {
      this._amplitudeService.sendToAmplitude({
        event_type: AmplitudeEvent.inAppCampaignClicked,
        user_id: userId,
        event_properties: {
          'Campaign Id': campaign.id,
          'Action Type': event.actionType ?? InAppCampaignActionType.none,
        },
      });
    } else {
      this._amplitudeService.sendToAmplitude({
        event_type: AmplitudeEvent.inAppCampaignDismissed,
        user_id: userId,
        event_properties: { 'Campaign Id': campaign.id },
      });
    }
  }

  private _tryResolve(): void {
    const user = this._dataService.getUser();
    if (!user || user.superUser) return; // super admin never sees campaigns (MOD-3)
    if (!this._dataService.isBuildingAdmin()) return; // resident/non-admin short-circuit (MOD-3)

    // Never a second modal on top of the one the admin is still looking at.
    if (this.campaign) return;

    const buildingId = this._currentBuildingId();
    // No building yet (the emission that beat the session bootstrap): nothing to latch on, and a
    // later emission will carry one.
    if (!buildingId) return;
    if (this._resolvedBuildingIds.has(buildingId)) return;
    // Latched BEFORE the request, not in the callback: `setBuilding()` can emit twice within the
    // same tick, and two in-flight calls would each reserve their own campaign.
    this._resolvedBuildingIds.add(buildingId);

    this._service.getNext().subscribe((campaign) => {
      if (!campaign) return; // data:null (or a swallowed failure) — render nothing, silently (MOD-4)
      this.campaign = campaign;
      this._amplitudeService.sendToAmplitude({
        event_type: AmplitudeEvent.inAppCampaignShown,
        user_id: this._currentUserId(),
        event_properties: { 'Campaign Id': campaign.id, 'Building Id': buildingId },
      });
    });
  }

  private _currentUserId(): string {
    const user = this._dataService.getUser();
    return (user?.id ?? user?._id ?? '') as string;
  }

  private _currentBuildingId(): string {
    const building = this._dataService.getBuilding();
    return (building?.id ?? building?._id ?? '') as string;
  }
}
